import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#050505] flex items-center justify-center" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md mx-auto px-6"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 text-sm"
        >
          <ArrowLeft size={16} /> Back to Login
        </Link>

        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl text-white mb-2">Reset Your Password</h1>
          <p className="text-white/60 text-sm">
            Enter your email address and we&apos;ll send you instructions to reset your password.
          </p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-heading text-xl text-white mb-2">Check Your Email</h2>
              <p className="text-white/60 text-sm mb-6">
                If an account exists for {email}, you will receive password reset instructions within a few minutes. Be sure to check your spam folder.
              </p>

              <Link 
                to="/login" 
                className="inline-flex items-center justify-center mt-6 text-[#D4AF37] hover:underline text-sm"
              >
                Return to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 py-3 pl-8 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors"
                    placeholder="your@email.com"
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors flex items-center justify-center"
                data-testid="forgot-submit"
              >
                {loading ? (
                  <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                ) : (
                  'Send Reset Instructions'
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};
